import { useEffect, useState, useRef } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import {
  ArrowLeft,
  RefreshCw,
  CheckCircle2,
  Clock,
  Send,
  User,
  Headphones,
  FileText,
  Calendar,
  Hash,
  FolderOpen,
  Download,
} from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Separator } from "@/components/ui/separator";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import { Label } from "@/components/ui/label";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { useSupportTickets } from "@/providers/SupportTicketsProvider";
import type {
  SupportTicketMessage,
  SupportTicketFile,
} from "@shared/types/SupportTicketDto";

const RESOLVE_REASONS = [
  { id: "issue-fixed", label: "The issue has been fixed" },
  { id: "workaround-found", label: "Found a workaround" },
  { id: "no-longer-needed", label: "No longer needed" },
  { id: "resolved-by-support", label: "Resolved with support team's help" },
  { id: "other", label: "Other" },
];

function MessageAttachment({ attachment }: { attachment: SupportTicketFile }) {
  return (
    <a
      href={attachment.fileUrl}
      target="_blank"
      rel="noopener noreferrer"
      className="flex items-center gap-2 p-2 rounded-lg bg-white/5 border border-white/10 hover:bg-white/10 transition-colors text-sm"
    >
      <FileText className="w-4 h-4 text-muted-foreground" />
      <span className="truncate flex-1">{attachment.fileName}</span>
      <Download className="w-4 h-4 text-muted-foreground" />
    </a>
  );
}

function MessageBubble({ message }: { message: SupportTicketMessage }) {
  return (
    <div className="flex gap-3 flex-row">
      <div className="w-8 h-8 rounded-full flex items-center justify-center shrink-0 bg-emerald-400/20">
        <Headphones className="w-4 h-4 text-emerald-400" />
      </div>
      <div className="max-w-[70%] items-start">
        <div className="rounded-lg px-4 py-3 bg-muted">
          <p className="text-sm whitespace-pre-wrap">{message.message}</p>
        </div>

        {message.attachments && message.attachments.length > 0 && (
          <div className="mt-2 space-y-1">
            {message.attachments.map((attachment) => (
              <MessageAttachment key={attachment.id} attachment={attachment} />
            ))}
          </div>
        )}

        <div className="flex items-center gap-2 mt-1 text-xs text-muted-foreground justify-start">
          <span>{message.senderName}</span>
          <span>•</span>
          <span>{new Date(message.createdAt).toLocaleString()}</span>
        </div>
      </div>
    </div>
  );
}

export function TicketDetailPage() {
  const { ticketId } = useParams<{ ticketId: string }>();
  const navigate = useNavigate();
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const {
    currentTicket,
    isLoading,
    fetchTicket,
    addMessage,
    resolveTicket,
    refreshCurrentTicket,
  } = useSupportTickets();

  const [newMessage, setNewMessage] = useState("");
  const [isSending, setIsSending] = useState(false);
  const [resolveDialogOpen, setResolveDialogOpen] = useState(false);
  const [selectedReason, setSelectedReason] = useState("");
  const [isResolving, setIsResolving] = useState(false);

  useEffect(() => {
    if (ticketId) {
      fetchTicket(ticketId);
    }
  }, [ticketId, fetchTicket]);

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [currentTicket?.messages]);

  async function handleSendMessage() {
    if (!newMessage.trim() || !ticketId) return;

    setIsSending(true);
    try {
      await addMessage(ticketId, newMessage.trim());
      setNewMessage("");
    } catch (error) {
      console.error("Failed to send message:", error);
    } finally {
      setIsSending(false);
    }
  }

  async function handleResolve() {
    if (!ticketId || !selectedReason) return;

    setIsResolving(true);
    try {
      await resolveTicket(ticketId, selectedReason);
      setResolveDialogOpen(false);
    } catch (error) {
      console.error("Failed to resolve ticket:", error);
    } finally {
      setIsResolving(false);
    }
  }

  function handleRefresh() {
    refreshCurrentTicket();
  }

  if (isLoading && !currentTicket) {
    return (
      <div className="flex items-center justify-center h-64">
        <RefreshCw className="h-8 w-8 animate-spin text-muted-foreground" />
      </div>
    );
  }

  if (!currentTicket) {
    return (
      <div className="text-center py-12">
        <p className="text-muted-foreground">Ticket not found</p>
        <Button
          variant="link"
          onClick={() => navigate("/support")}
          className="mt-4"
        >
          Back to Support
        </Button>
      </div>
    );
  }

  const isTicketResolved = currentTicket.isResolved;

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      className="space-y-6"
    >
      <div className="flex items-center justify-between">
        <Button
          variant="ghost"
          onClick={() => navigate("/support")}
          className="gap-2"
        >
          <ArrowLeft className="h-4 w-4" />
          Back to Support
        </Button>
        <Button variant="outline" size="icon" onClick={handleRefresh}>
          <RefreshCw className={`h-4 w-4 ${isLoading ? "animate-spin" : ""}`} />
        </Button>
      </div>

      <Card className="glass-card border-gradient">
        <CardHeader className="pb-4">
          <div className="flex items-start justify-between gap-4">
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2 mb-2">
                <Badge
                  className={
                    currentTicket.isResolved
                      ? "bg-emerald-400/20 text-emerald-400"
                      : "bg-amber-400/20 text-amber-400"
                  }
                >
                  {currentTicket.isResolved ? (
                    <CheckCircle2 className="h-3 w-3 mr-1" />
                  ) : (
                    <Clock className="h-3 w-3 mr-1" />
                  )}
                  {currentTicket.isResolved ? "Resolved" : "Open"}
                </Badge>
                {currentTicket.isManuallyResolved && (
                  <Badge variant="secondary">Admin Resolved</Badge>
                )}
              </div>
              <CardTitle className="text-xl">
                {currentTicket.ticketName}
              </CardTitle>
            </div>
          </div>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 p-4 rounded-xl bg-white/[0.02] border border-white/5">
            <div className="flex items-center gap-2">
              <Hash className="h-4 w-4 text-muted-foreground" />
              <div>
                <p className="text-xs text-muted-foreground">Ticket ID</p>
                <p className="text-sm font-mono">{currentTicket.ticketId}</p>
              </div>
            </div>
            <div className="flex items-center gap-2">
              <FolderOpen className="h-4 w-4 text-muted-foreground" />
              <div>
                <p className="text-xs text-muted-foreground">Project</p>
                <p className="text-sm">
                  {currentTicket.project?.name || "N/A"}
                </p>
              </div>
            </div>
            <div className="flex items-center gap-2">
              <Calendar className="h-4 w-4 text-muted-foreground" />
              <div>
                <p className="text-xs text-muted-foreground">Created</p>
                <p className="text-sm">
                  {new Date(currentTicket.createdAt).toLocaleDateString()}
                </p>
              </div>
            </div>
            {currentTicket.assignee && (
              <div className="flex items-center gap-2">
                <User className="h-4 w-4 text-muted-foreground" />
                <div>
                  <p className="text-xs text-muted-foreground">Assigned To</p>
                  <p className="text-sm">
                    {currentTicket.assignee.firstName}{" "}
                    {currentTicket.assignee.lastNameAbr}.
                  </p>
                </div>
              </div>
            )}
          </div>

          {currentTicket.ticketDescription && (
            <div className="p-4 rounded-xl bg-white/[0.02] border border-white/5">
              <p className="text-xs text-muted-foreground uppercase tracking-wide mb-2">
                Description
              </p>
              <p className="text-sm whitespace-pre-wrap">
                {currentTicket.ticketDescription}
              </p>
            </div>
          )}

          {currentTicket.resolvedReason && (
            <div className="p-4 rounded-xl bg-emerald-500/5 border border-emerald-500/10">
              <p className="text-xs text-emerald-400 uppercase tracking-wide mb-2">
                Resolution Reason
              </p>
              <p className="text-sm text-emerald-300">
                {currentTicket.resolvedReason}
              </p>
            </div>
          )}
        </CardContent>
      </Card>

      <Card className="glass-card border-gradient">
        <CardHeader>
          <CardTitle className="text-lg">Messages</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="space-y-6 max-h-96 overflow-y-auto mb-6 sleek-scrollbar">
            {currentTicket.messages && currentTicket.messages.length > 0 ? (
              currentTicket.messages.map((message) => (
                <MessageBubble key={message.createdAt} message={message} />
              ))
            ) : (
              <p className="text-center text-muted-foreground py-8">
                No messages yet. Start the conversation!
              </p>
            )}
            <div ref={messagesEndRef} />
          </div>

          <Separator className="my-4" />

          {isTicketResolved ? (
            <div className="p-4 rounded-xl bg-muted/30 border border-white/5 text-center">
              <CheckCircle2 className="h-6 w-6 text-emerald-400 mx-auto mb-2" />
              <p className="text-sm text-muted-foreground">
                This ticket has been resolved. Messaging is disabled.
              </p>
            </div>
          ) : (
            <div className="space-y-4">
              <Textarea
                placeholder="Type your message..."
                value={newMessage}
                onChange={(e) => setNewMessage(e.target.value)}
                rows={3}
                onKeyDown={(e) => {
                  if (e.key === "Enter" && (e.metaKey || e.ctrlKey)) {
                    handleSendMessage();
                  }
                }}
              />
              <div className="flex justify-end">
                <Button
                  onClick={handleSendMessage}
                  disabled={!newMessage.trim() || isSending}
                >
                  <Send className="h-4 w-4 mr-2" />
                  {isSending ? "Sending..." : "Send Message"}
                </Button>
              </div>
            </div>
          )}
        </CardContent>
      </Card>

      {!isTicketResolved && (
        <Card className="glass-card border-gradient">
          <CardHeader>
            <CardTitle className="text-lg">Mark as Resolved</CardTitle>
          </CardHeader>
          <CardContent>
            <p className="text-sm text-muted-foreground mb-4">
              If your issue has been resolved, you can close this ticket.
            </p>
            <Button
              variant="outline"
              onClick={() => setResolveDialogOpen(true)}
              className="gap-2"
            >
              <CheckCircle2 className="h-4 w-4" />
              Mark as Resolved
            </Button>
          </CardContent>
        </Card>
      )}

      {currentTicket.isManuallyResolved && currentTicket.resolvedAt && (
        <Card className="glass-card border-gradient border-l-4 border-l-emerald-400">
          <CardContent className="p-6">
            <div className="flex items-center gap-3">
              <CheckCircle2 className="h-5 w-5 text-emerald-400" />
              <p className="text-sm">
                This ticket was resolved by our support team on{" "}
                {new Date(currentTicket.resolvedAt).toLocaleDateString()}
              </p>
            </div>
          </CardContent>
        </Card>
      )}

      <Dialog open={resolveDialogOpen} onOpenChange={setResolveDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Resolve Ticket</DialogTitle>
          </DialogHeader>
          <div className="py-4">
            <Label className="text-sm text-muted-foreground mb-4 block">
              Please select a reason for closing this ticket:
            </Label>
            <RadioGroup
              value={selectedReason}
              onValueChange={setSelectedReason}
              className="space-y-3"
            >
              {RESOLVE_REASONS.map((reason) => (
                <div key={reason.id} className="flex items-center space-x-3">
                  <RadioGroupItem value={reason.id} id={reason.id} />
                  <Label htmlFor={reason.id} className="cursor-pointer">
                    {reason.label}
                  </Label>
                </div>
              ))}
            </RadioGroup>
          </div>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setResolveDialogOpen(false)}
            >
              Cancel
            </Button>
            <Button
              onClick={handleResolve}
              disabled={!selectedReason || isResolving}
            >
              {isResolving ? "Resolving..." : "Confirm Resolution"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </motion.div>
  );
}
